import { computedFrom } from "aurelia-binding";
import { DialogController } from "aurelia-dialog";
import { autoinject, observable } from "aurelia-framework";
import { Router } from "aurelia-router";

import nameof from "../../../common/nameof";
import { IGetAccountsModel } from "../../../interfaces/i-get-accounts";
import { AuthService } from "../../../services/auth-service";
import { IGetProviderDefault } from "../../../services/i-providers-service";
import Infrastructure from "../../../common/infrastructure";
import { LineOfServiceEnum } from "../../../enums/line-of-service-enum";

@autoinject
export class SwitchAccountDialog {
    private readonly _dialogController: DialogController;
    private readonly _authService: AuthService;
    private _router: Router;
    @observable({
        changeHandler: nameof<SwitchAccountDialog>("selectedAccountChanged")
    })
    public selectedAccount: IGetAccountsModel = null;
    public showHospice: number = null;
    public accounts: IGetAccountsModel[];
    public currentAccountId: string;
    public searchQuery: string;
    public providers: IGetProviderDefault[];
    public selectedProviders: IGetProviderDefault[];
    public rememberSelection: boolean = false;
    public isProvidersLoading: boolean = false;
    public defaultProviders: string[] = [];
    public axxessCentralUrl: string = Infrastructure.axxessCentral;
    public lineOfServiceEnum: typeof LineOfServiceEnum = LineOfServiceEnum;

    @computedFrom(nameof<SwitchAccountDialog>("providers"))
    public get showLineOfServiceToggle() {
        let userHasPalliativeCareProviders = this.providers?.some(
            (provider) => provider.lineOfService === this.lineOfServiceEnum.PalliativeCare
        );
        let userHasHospiceProviders = this.providers?.some(
            (provider) => provider.lineOfService === this.lineOfServiceEnum.Hospice
        );
        return userHasPalliativeCareProviders && userHasHospiceProviders;
    }

    @computedFrom(
        `${nameof<SwitchAccountDialog>("selectedProviders")}.length`,
        `${nameof<SwitchAccountDialog>("defaultProviders")}.length`
    )
    public get allProvidersChecked() {
        return this.selectedProviders?.length === this.defaultProviders.length;
    }

    @computedFrom(nameof<SwitchAccountDialog>("showHospice"))
    public get isHospiceActive() {
        return this.showHospice === this.lineOfServiceEnum.Hospice;
    }

    public constructor(dialogController: DialogController, authService: AuthService, router: Router) {
        this._dialogController = dialogController;
        this._authService = authService;
        this._router = router;
    }

    public activate(params: SwitchAccountDialogParams) {
        this.accounts = params.accounts;
        this.selectedAccount = this.accounts.find((f) => f.id == params.currentAccountId);
    }

    public async selectedAccountChanged(account: IGetAccountsModel = null) {
        if (!account) {
            return;
        }
        this.isProvidersLoading = true;
        this.providers = account.providers;
        if (this.providers?.length > 0) {
            let rememberedSelection = this.providers.find((provider) => provider.isDefault);
            if (!!rememberedSelection) {
                this.updateShowHospice(rememberedSelection.lineOfService);
            } else {
                this.updateShowHospice(this.providers[0].lineOfService);
            }
        }
        this.isProvidersLoading = false;
    }

    public updateShowHospice(value: number) {
        this.showHospice = value;
        this.updateSelectedProviders();
    }

    public updateSelectedProviders() {
        if (!this.providers || this.providers?.length === 0) {
            return;
        }
        this.defaultProviders?.splice(0, this.defaultProviders?.length);
        this.selectedProviders = this.providers.filter((provider) => provider.lineOfService === this.showHospice);
        this.selectedProviders.forEach((provider) => {
            if (provider.isDefault) {
                this.defaultProviders.push(provider.id);
            }
        });
    }

    public navigateToAxxessCentral() {
        this._router.navigate(this.axxessCentralUrl);
    }

    public async closeDialog() {
        this.providers.forEach((provider) => {
            provider.isDefault = this.defaultProviders.includes(provider.id);
        });
        let selectedInfo = {
            accountId: this.selectedAccount.id,
            providers: this.providers,
            rememberSelection: this.rememberSelection,
            lineOfService: this.selectedProviders?.[0].lineOfService,
            clusterId: this.selectedAccount.clusterId
        };
        this._dialogController.ok(selectedInfo);
    }

    public toggleAllSelected() {
        let checkAll = !this.allProvidersChecked;
        this.defaultProviders?.splice(0, this.defaultProviders?.length);
        if (checkAll) {
            this.selectedProviders.forEach((provider) => {
                this.defaultProviders.push(provider.id);
            });
        }
    }
}

export class SwitchAccountDialogParams {
    public accounts: IGetAccountsModel[];
    public currentAccountId: string;
}
